import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, GraduationCap, BookOpen, Award, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseManagementPage = () => {
  const [courses, setCourses] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');
  
  // Course dialog state
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    name: '',
    description: '',
    duration_hours: '',
    category: 'professional_development',
    is_active: true,
    is_required: false,
    certification_id: ''
  });
  const [saving, setSaving] = useState(false);

  // Certification dialog state
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [certForm, setCertForm] = useState({
    name: '',
    description: '',
    validity_months: 12,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, certsRes] = await Promise.all([
        axios.get(`${API}/admin/courses`),
        axios.get(`${API}/admin/certifications`)
      ]);
      setCourses(coursesRes.data.courses || []);
      setCertifications(certsRes.data.certifications || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCourseDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setCourseForm({
        name: course.name,
        description: course.description || '',
        duration_hours: course.duration_hours || '',
        category: course.category || 'professional_development',
        is_active: course.is_active !== false,
        is_required: course.is_required || false,
        certification_id: course.certification_id || ''
      });
    } else {
      setEditingCourse(null);
      setCourseForm({
        name: '',
        description: '',
        duration_hours: '',
        category: 'professional_development',
        is_active: true,
        is_required: false,
        certification_id: ''
      });
    }
    setCourseDialogOpen(true);
  };

  const saveCourse = async () => {
    if (!courseForm.name.trim()) {
      toast.error('Course name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCourse) {
        await axios.put(`${API}/admin/courses/${editingCourse.id}`, courseForm);
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API}/admin/courses`, courseForm);
        toast.success('Course created successfully');
      }
      setCourseDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`);
      toast.success('Course deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const openCertDialog = (cert = null) => {
    if (cert) {
      setEditingCert(cert);
      setCertForm({
        name: cert.name,
        description: cert.description || '',
        validity_months: cert.validity_months || 12,
        is_active: cert.is_active !== false
      });
    } else {
      setEditingCert(null);
      setCertForm({
        name: '',
        description: '',
        validity_months: 12,
        is_active: true
      });
    }
    setCertDialogOpen(true);
  };

  const saveCertification = async () => {
    if (!certForm.name.trim()) {
      toast.error('Certification name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCert) {
        await axios.put(`${API}/admin/certifications/${editingCert.id}`, certForm);
        toast.success('Certification updated successfully');
      } else {
        await axios.post(`${API}/admin/certifications`, certForm);
        toast.success('Certification created successfully');
      }
      setCertDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save certification');
    } finally {
      setSaving(false);
    }
  };

  const COURSE_CATEGORIES = [
    { value: 'professional_development', label: 'Professional Development' },
    { value: 'compliance', label: 'Compliance & Safety' },
    { value: 'technical', label: 'Technical Skills' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'onboarding', label: 'Onboarding' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="course-management-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="w-6 h-6" />
              Course Management
            </h1>
            <p className="text-muted-foreground">Manage courses and certifications</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button
          variant={activeTab === 'courses' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('courses')}
          className="rounded-b-none"
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Courses ({courses.length})
        </Button>
        <Button
          variant={activeTab === 'certifications' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('certifications')}
          className="rounded-b-none"
        >
          <Award className="w-4 h-4 mr-2" />
          Certifications ({certifications.length})
        </Button>
      </div>

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Training Courses</CardTitle>
              <CardDescription>Manage available training courses</CardDescription>
            </div>
            <Button onClick={() => openCourseDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Course
            </Button>
          </CardHeader>
          <CardContent>
            {courses.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Course Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {courses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell className="font-medium">{course.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {COURSE_CATEGORIES.find(c => c.value === course.category)?.label || course.category}
                        </Badge>
                      </TableCell>
                      <TableCell>{course.duration_hours ? `${course.duration_hours} hrs` : '-'}</TableCell>
                      <TableCell>
                        {course.is_required ? (
                          <Badge className="bg-red-100 text-red-800">Required</Badge>
                        ) : (
                          <span className="text-muted-foreground">Optional</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={course.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {course.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openCourseDialog(course)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCourse(course.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No courses created yet</p>
                <Button className="mt-4" onClick={() => openCourseDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Course
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Certifications Tab */}
      {activeTab === 'certifications' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Certifications</CardTitle>
              <CardDescription>Manage certification types</CardDescription>
            </div>
            <Button onClick={() => openCertDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Certification
            </Button>
          </CardHeader>
          <CardContent>
            {certifications.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Certification Name</TableHead>
                    <TableHead>Validity Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certifications.map((cert) => (
                    <TableRow key={cert.id}>
                      <TableCell className="font-medium">{cert.name}</TableCell>
                      <TableCell>{cert.validity_months} months</TableCell>
                      <TableCell>
                        <Badge className={cert.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {cert.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openCertDialog(cert)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No certifications created yet</p>
                <Button className="mt-4" onClick={() => openCertDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Certification
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Course Dialog */}
      <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'Add Course'}</DialogTitle>
            <DialogDescription>Configure course details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Course Name *</Label>
              <Input
                value={courseForm.name}
                onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                placeholder="Enter course name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                placeholder="Course description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={courseForm.category}
                  onValueChange={(v) => setCourseForm({ ...courseForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COURSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  value={courseForm.duration_hours}
                  onChange={(e) => setCourseForm({ ...courseForm, duration_hours: e.target.value })}
                  placeholder="e.g., 4"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Required Course</p>
                <p className="text-sm text-muted-foreground">All staff must complete this course</p>
              </div>
              <Switch
                checked={courseForm.is_required}
                onCheckedChange={(v) => setCourseForm({ ...courseForm, is_required: v })}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Course is available for enrollment</p>
              </div>
              <Switch
                checked={courseForm.is_active}
                onCheckedChange={(v) => setCourseForm({ ...courseForm, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCourse} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCourse ? 'Update' : 'Create'} Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certification Dialog */}
      <Dialog open={certDialogOpen} onOpenChange={setCertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCert ? 'Edit Certification' : 'Add Certification'}</DialogTitle>
            <DialogDescription>Configure certification details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Certification Name *</Label>
              <Input
                value={certForm.name}
                onChange={(e) => setCertForm({ ...certForm, name: e.target.value })}
                placeholder="Enter certification name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={certForm.description}
                onChange={(e) => setCertForm({ ...certForm, description: e.target.value })}
                placeholder="Certification description"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Validity Period (months)</Label>
              <Input
                type="number"
                value={certForm.validity_months}
                onChange={(e) => setCertForm({ ...certForm, validity_months: parseInt(e.target.value) || 12 })}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Certification can be issued</p>
              </div>
              <Switch
                checked={certForm.is_active}
                onCheckedChange={(v) => setCertForm({ ...certForm, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCertification} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCert ? 'Update' : 'Create'} Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseManagementPage;
